import {
  collection,
  collectionGroup,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase/client";
import type { CaseFingerprint, Patient } from "../contexts/types";

export interface SimilarCaseMatch {
  caseId: string;
  bone: string;
  classification: string;
  treatmentStrategy: string;
  implantType?: string;
  fixationType: string;
  healingStatus?: string;
  complications?: string;
  followUpMonths?: number;
}

export interface SimilarCasesSummary {
  totalCases: number;
  mostCommonTreatmentStrategy: string;
  mostCommonImplantType: string | null;
  averageHealingWeeks: number | null;
  complicationRate: number | null;
  unionRate: number | null;
  registrySuggestion: string | null;
  hasSufficientEvidence: boolean;
}

type FindSimilarCasesOptions = {
  currentCaseId?: string;
  maxResults?: number;
  userId?: string;
};

const normalize = (value?: string) => (value || "").trim().toLowerCase();

const getMostCommonValue = (values: string[]) => {
  const counts = values.reduce((acc, value) => {
    const normalized = value.trim();
    if (!normalized) return acc;
    acc[normalized] = (acc[normalized] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const [mostCommon] = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return mostCommon?.[0] || "";
};

const toMatch = (patient: Patient): SimilarCaseMatch => ({
  caseId: patient.id,
  bone: patient.caseFingerprint?.bone || "",
  classification: patient.caseFingerprint?.classification || "",
  treatmentStrategy: patient.caseFingerprint?.treatmentStrategy || "",
  implantType: patient.clinicalRegistry?.treatment.implantType || "",
  fixationType: patient.caseFingerprint?.fixationType || "",
  healingStatus: patient.clinicalRegistry?.outcome.healingStatus || "",
  complications: patient.clinicalRegistry?.outcome.complications || "",
  followUpMonths: patient.clinicalRegistry?.outcome.followUpMonths,
});

const isSimilarCase = (
  candidate: Patient,
  currentCaseFingerprint: CaseFingerprint,
  currentCaseId?: string
) => {
  if (!candidate.caseFingerprint) return false;
  if (candidate.id === currentCaseId) return false;
  if (candidate.dataSharingMode === "private") return false;

  const currentBone = normalize(currentCaseFingerprint.bone);
  const candidateBone = normalize(candidate.caseFingerprint.bone);

  if (!currentBone || !candidateBone || currentBone !== candidateBone) {
    return false;
  }

  const currentClassification = normalize(currentCaseFingerprint.classification);
  const candidateClassification = normalize(candidate.caseFingerprint.classification);
  const currentStrategy = normalize(currentCaseFingerprint.treatmentStrategy);
  const candidateStrategy = normalize(candidate.caseFingerprint.treatmentStrategy);

  return (
    (!!currentClassification &&
      !!candidateClassification &&
      currentClassification === candidateClassification) ||
    (!!currentStrategy &&
      !!candidateStrategy &&
      currentStrategy === candidateStrategy)
  );
};

const scoreSimilarCase = (candidate: Patient, currentCaseFingerprint: CaseFingerprint) => {
  let score = 0;

  if (
    normalize(candidate.caseFingerprint?.classification) ===
    normalize(currentCaseFingerprint.classification)
  ) {
    score += 2;
  }

  if (
    normalize(candidate.caseFingerprint?.treatmentStrategy) ===
    normalize(currentCaseFingerprint.treatmentStrategy)
  ) {
    score += 1;
  }

  return score;
};

async function loadCandidatePatients(userId?: string): Promise<Patient[]> {
  try {
    const sharedCasesQuery = query(
      collectionGroup(db, "patients"),
      where("dataSharingMode", "in", ["anonymous_registry", "research"]),
      limit(200)
    );
    const snap = await getDocs(sharedCasesQuery);

    return snap.docs.map((docSnap) => ({
      id: docSnap.id,
      ...(docSnap.data() as any),
    })) as Patient[];
  } catch (error) {
    const resolvedUserId = userId || auth.currentUser?.uid;
    if (!resolvedUserId) {
      return [];
    }

    const userPatientsQuery = query(
      collection(db, "users", resolvedUserId, "patients"),
      limit(200)
    );
    const snap = await getDocs(userPatientsQuery);

    return snap.docs.map((docSnap) => ({
      id: docSnap.id,
      ...(docSnap.data() as any),
    })) as Patient[];
  }
}

export async function findSimilarCases(
  currentCaseFingerprint?: CaseFingerprint,
  options: FindSimilarCasesOptions = {}
): Promise<SimilarCaseMatch[]> {
  if (!currentCaseFingerprint?.bone) {
    return [];
  }

  const candidates = await loadCandidatePatients(options.userId);

  return candidates
    .filter((candidate) =>
      isSimilarCase(candidate, currentCaseFingerprint, options.currentCaseId)
    )
    .sort(
      (a, b) =>
        scoreSimilarCase(b, currentCaseFingerprint) -
        scoreSimilarCase(a, currentCaseFingerprint)
    )
    .slice(0, options.maxResults ?? 10)
    .map(toMatch);
}

export function summarizeSimilarCases(
  similarCases: SimilarCaseMatch[]
): SimilarCasesSummary {
  const totalCases = similarCases.length;
  const strategyCounts = similarCases.reduce((acc, item) => {
    const strategy = item.treatmentStrategy.trim();
    if (!strategy) return acc;
    acc[strategy] = (acc[strategy] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const mostCommonTreatmentStrategy = getMostCommonValue(
    similarCases.map((item) => item.treatmentStrategy)
  );
  const mostCommonTreatmentCount = mostCommonTreatmentStrategy
    ? strategyCounts[mostCommonTreatmentStrategy] || 0
    : 0;
  const casesWithImplantType = similarCases.filter((item) => !!normalize(item.implantType));
  const mostCommonImplantType =
    casesWithImplantType.length >= 3
      ? getMostCommonValue(casesWithImplantType.map((item) => item.implantType || ""))
      : null;

  const complicationCasesCount = similarCases.filter((item) => {
    const value = normalize(item.complications);
    return !!value && value !== "none";
  }).length;

  const complicationRate =
    totalCases >= 5 ? Math.round((complicationCasesCount / totalCases) * 100) : null;

  const casesWithHealingStatus = similarCases.filter(
    (item) => !!normalize(item.healingStatus)
  );
  const unionCasesCount = casesWithHealingStatus.filter(
    (item) => normalize(item.healingStatus) === "union"
  ).length;

  const unionRate =
    casesWithHealingStatus.length >= 3
      ? Math.round((unionCasesCount / casesWithHealingStatus.length) * 100)
      : null;
  const unionCasesWithFollowUp = similarCases.filter(
    (item) =>
      normalize(item.healingStatus) === "union" &&
      typeof item.followUpMonths === "number" &&
      item.followUpMonths > 0
  );
  const averageHealingWeeks =
    unionCasesWithFollowUp.length >= 3
      ? Math.round(
          unionCasesWithFollowUp.reduce(
            (sum, item) => sum + (item.followUpMonths || 0) * 4.3,
            0
          ) / unionCasesWithFollowUp.length
        )
      : null;
  const registrySuggestion =
    totalCases >= 5 && mostCommonTreatmentCount / totalCases > 0.5
      ? mostCommonTreatmentStrategy
      : "Registro clínico sem predominância clara de estratégia.";

  return {
    totalCases,
    mostCommonTreatmentStrategy,
    mostCommonImplantType,
    averageHealingWeeks,
    complicationRate,
    unionRate,
    registrySuggestion,
    hasSufficientEvidence:
      totalCases >= 5 &&
      !!mostCommonTreatmentStrategy &&
      complicationRate !== null &&
      unionRate !== null,
  };
}
